import { useTranslation } from 'react-i18next';
import { CopyIcon } from 'lucide-react';
import { toast } from '@noor/lib';
import { useLegacyEquipments } from '@/entities/legacy-order-draft';
import {
  legacyFormatPhone,
  legacyFileUrl,
  legacyProductPaidLabelKey,
  LegacyTimeCountdown,
  type LegacyOrderDetail,
} from '@/entities/legacy-order';
import { legacyDebtCountdown } from '../lib/order-detail';
import { LegacyInfoBox } from './legacy-info-box';

/**
 * The three summary cards — delivery terms, courier, client — reproducing the
 * old panel's `OrderOtherInfo`.
 *
 * Several of the source's fallbacks are deliberately odd and are kept:
 * a delivery `type` that is anything other than `EXPRESS` (including null)
 * reads as "scheduled", and a `payment_type` that is neither `CASH` nor
 * `BALANCE` reads as "card". Those are the old panel's own defaults, and a
 * merchant comparing the two screens should see the same words.
 */
export function LegacyOrderOtherInfo({
  order,
  onPreviewImages,
}: {
  order: LegacyOrderDetail;
  /** Opens the drawer-level lightbox (the receipt thumbnail's click). */
  onPreviewImages: (images: string[]) => void;
}) {
  const { t } = useTranslation('legacy');
  const { equipments } = useLegacyEquipments();

  const equipmentName = equipments.find(
    (e) => e.id === order.delivery.equipment_id,
  )?.name;

  const paymentLabel =
    order.payment_type === 'CASH'
      ? t('single.cash')
      : order.payment_type === 'BALANCE'
        ? t('single.balance')
        : t('single.card');

  const deliveryLabel = `${
    order.delivery.type === 'EXPRESS'
      ? t('single.delivery_express')
      : t('single.delivery_scheduled')
  }${order.delivery.door_to_door ? ` (${t('single.door_to_door')})` : ''}`;

  const courierName = order.courier
    ? [order.courier.first_name, order.courier.last_name].filter(Boolean).join(' ')
    : t('single.not_defined');

  const copyCourierPhone = () => {
    // The source copies the RAW number, not the masked display value — so a
    // paste lands as `+998…` ready to dial.
    if (order.courier?.phone) {
      void navigator.clipboard.writeText(order.courier.phone);
      toast.success(t('toast.copied'));
    }
  };

  const debt = legacyDebtCountdown(order);
  const receiptUrl = legacyFileUrl(order.courier_payment_receipt_url);

  // The 5px gap under 700px is the source's own `@media` quirk.
  return (
    <div className="flex w-full flex-col gap-5 max-[700px]:gap-[5px] min-[601px]:flex-row">
      {/* Delivery terms */}
      <Card>
        <LegacyInfoBox title={t('single.delivery_type')} info={deliveryLabel} />
        <LegacyInfoBox title={t('single.payment_type')} info={paymentLabel} />
        <LegacyInfoBox
          title={t('single.check')}
          info={
            order.pricing.ofd_check_url ? (
              <a
                href={order.pricing.ofd_check_url}
                target="_blank"
                rel="noreferrer"
                className="cursor-pointer text-[var(--lg-orange-500)]"
              >
                {t('single.ofd')}
              </a>
            ) : (
              t('single.ofd_proccess')
            )
          }
        />
      </Card>

      {/* Courier */}
      <Card>
        <LegacyInfoBox title={t('single.courier')} info={courierName} />
        <div className="flex items-end gap-2">
          <LegacyInfoBox
            title={t('single.courier_phone')}
            info={
              order.courier ? (
                <span className="whitespace-nowrap">
                  {legacyFormatPhone(order.courier.phone)}
                </span>
              ) : (
                t('single.not_defined')
              )
            }
          />
          {order.courier && (
            <button
              type="button"
              onClick={copyCourierPhone}
              title={t('single.copy')}
              aria-label={t('single.copy')}
              className="mb-0.5 cursor-pointer rounded-lg bg-[var(--lg-grey-100)] p-1.5"
            >
              <CopyIcon className="size-3.5" aria-hidden="true" />
            </button>
          )}
        </div>
        <LegacyInfoBox
          title={t('single.equipment_type')}
          info={equipmentName ?? t('single.not_defined')}
        />
      </Card>

      {/* Client — the merchant account that PLACED the order (`vendor`), not
          the pickup contact. The " - Business" suffix is the source's literal
          (`OrderOtherInfo.tsx:189`), appended even to an empty name. */}
      <Card>
        <LegacyInfoBox
          title={t('single.client')}
          info={`${order.vendor?.name ?? ''} - Business`}
        />
        <LegacyInfoBox
          title={t('single.client_phone')}
          info={
            <span className="whitespace-nowrap">
              {legacyFormatPhone(order.vendor?.phone)}
            </span>
          }
        />

        <div className="flex items-center gap-1">
          <LegacyInfoBox
            title={t('single.product_paid')}
            info={
              <span className="flex flex-col">
                {/* The debt countdowns, stacked ABOVE the status label
                    (`OrderOtherInfo.tsx:196-264`). */}
                {debt?.kind === 'debt' && (
                  <LegacyTimeCountdown color="red" endTime={debt.endTime} />
                )}
                {debt?.kind === 'debt-review' && (
                  <LegacyTimeCountdown
                    onlyShow
                    color={debt.color}
                    endTime={debt.endTime}
                    doneAt={debt.doneAt}
                  />
                )}
                {t(
                  legacyProductPaidLabelKey({
                    totalProductPrice: order.total_product_price,
                    productPaid: order.delivery.product_paid,
                    courierPaymentStatus: order.courier_payment_status,
                  }),
                )}
              </span>
            }
          />

          {/* The courier's payment receipt — a 50px thumbnail that zooms into
              the lightbox (`OrderOtherInfo.tsx:278-294`). */}
          {order.courier_payment_receipt_url && receiptUrl && (
            <button
              type="button"
              onClick={() => {
                if (order.courier_payment_receipt_url) {
                  onPreviewImages([order.courier_payment_receipt_url]);
                }
              }}
              className="mt-2 shrink-0 cursor-zoom-in"
              aria-label={t('single.open_cheque')}
            >
              <img
                src={receiptUrl}
                alt=""
                className="max-h-[50px] w-[50px] rounded-[6px] object-contain"
              />
            </button>
          )}
        </div>
      </Card>
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-[206px] w-full flex-col gap-5 rounded-[20px] bg-[var(--lg-surface)] p-5">
      {children}
    </div>
  );
}
